import * as React from 'react';
import { jsx, css } from '@emotion/react';
import { mediaQueries, autoResponsiveClamp } from 'Src/me/pages/guest/proposals/new/React/viewHelpers';
import type { ContractorSearchInterfacePropsType } from '../..';
import { LogoSvg } from '../../assets/Logo.svg';

const { tablet, mobile } = mediaQueries();

type FooterPropsType = ContractorSearchInterfacePropsType;

export const Footer = (props: FooterPropsType) => {
  const featureItems = React.useMemo(() => {
    return [
      {
        id: 1,
        textElement: (
          <>
            サイト利用者数<br />
            <span
              css={css`
                font-size: 19px;
              `}
            >
              65
            </span>
            万人突破
          </>
        ),
      },
      {
        id: 2,
        textElement: (
          <>
            工事成約実績<br />
            <span
              css={css`
                font-size: 19px;
              `}
            >
              3
            </span>
            万件突破
          </>
        ),
      },
      {
        id: 3,
        textElement: (
          <>
            全国提携社数<br />
            <span
              css={css`
                font-size: 19px;
              `}
            >
              4,
            </span>
            <span
              css={css`
                font-size: 15px;
              `}
            >
              500
            </span>
            社以上
          </>
        ),
      },
    ];
  }, []);

  const linkItems = React.useMemo(() => {
    return [
      {
        id: 1,
        text: '運営会社',
        href: 'https://speee.jp/company/',
        newTab: true,
      },
      {
        id: 2,
        text: 'プライバシーポリシー',
        href: 'https://speee.jp/privacy/',
        newTab: true,
      },
      {
        id: 3,
        text: '加盟希望のお問い合わせ',
        href: 'https://biz.nuri-kae.jp',
        newTab: true,
      },
    ];
  }, []);

  return (
    <div
      css={css`
        border-top: 4px solid #ebebeb;

        ${tablet(() => css`
          border-top: none;
        `)}
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-evenly;
          align-items: center;
          max-width: 1000px;
          margin-inline: auto;
          padding: 40px 1em 36px;

          ${tablet(({ setAutomaticDesignBasedSize }) => css`
            flex-direction: column;
            gap: ${setAutomaticDesignBasedSize(24, 17)} 0;
            padding: ${setAutomaticDesignBasedSize(40, 24)} 1em ${setAutomaticDesignBasedSize(36, 30)};
          `)}

          ${mobile(({ setAutomaticDesignBasedSize }) => css`
            gap: ${setAutomaticDesignBasedSize(24, 17)} 0;
            padding: ${setAutomaticDesignBasedSize(40, 24)} 1em ${setAutomaticDesignBasedSize(36, 30)};
          `)}
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            flex-direction: column;
            gap: 12px 0;

            ${tablet(({ setAutomaticDesignBasedSize }) => css`
              min-width: 300px;
              padding-bottom: ${setAutomaticDesignBasedSize(24, 14)};
              border-bottom: 1px solid #ebebeb;
            `)}

            ${mobile(({ setAutomaticDesignBasedSize }) => css`
              padding-bottom: ${setAutomaticDesignBasedSize(24, 14)};
            `)}
          `}
        >
          <p
            css={css`
              color: #8ab0b8;
              font-size: 12px;

              ${mobile(({ setAutomaticDesignBasedSize }) => css`
                font-size: ${setAutomaticDesignBasedSize(12, 12)};
              `)}
            `}
          >
            あなたにあった信頼のリフォーム会社探しをお手伝い
          </p>
          <div
            css={css`
              font-size: 0;
              width: 206px;

              ${tablet(({ setAutomaticDesignBasedSize }) => css`
                width: ${setAutomaticDesignBasedSize(196, 169)};
              `)}

              ${mobile(({ setAutomaticDesignBasedSize }) => css`
                width: ${setAutomaticDesignBasedSize(196, 169)};
              `)}
            `}
          >
            <LogoSvg />
          </div>
        </div>
        <div
          css={css`
            width: fit-content;

            ${mobile(() => css`
              width: 100%;
              max-width: 350px;
            `)}
          `}
        >
          <ul
            css={css`
              display: grid;
              grid-template-columns: repeat(3, 100px);
              gap: 0 10px;

              ${mobile(() => css`
                grid-template-columns: repeat(3, 1fr);
              `)}
            `}
          >
            {featureItems.map((item) => {
              const { id, textElement } = item;
              return (
                <li
                  key={id}
                  css={css`
                    color: #3aaec6;
                    font-size: 10px;
                    font-weight: bold;
                    text-align: center;
                    padding: 7px 0;
                    background-color: #e3f6f5;
                    border-radius: 4px;
                  `}
                >
                  {textElement}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      <div
        css={css`
          background-color: #848484;
          border-top: 5px solid #b1b1b1;
        `}
      >
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 0 1em;
            max-width: 1000px;
            margin-inline: auto;
            padding: 16px 1em;

            ${mobile(() => css`
              flex-direction: column;
              gap: 1em 0;
              padding: 16px 1em 40px;
            `)}
          `}
        >
          <ul
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;
            `}
          >
            {linkItems.map((item) => {
              const { id, text, href, newTab } = item;

              return (
                <li
                  key={id}
                  css={css`
                    color: #fff;
                    font-size: 12px;
                    margin-right: 2em;
                    position: relative;

                    ${mobile(({ clamp }) => css`
                      font-size: ${clamp('10px', '12px')};
                    `)}

                    &::before {
                      content: "｜";
                      position: absolute;
                      right: -1.5em;
                      top: 0;
                    }

                    &:last-of-type {
                      margin-right: 0;

                      &::before {
                        display: none;
                      }
                    }
                  `}
                >
                  <a
                    href={href}
                    target={newTab ? '_blank' : undefined}
                    rel={newTab ? 'noreferrer' : undefined}
                    css={css`
                      color: #fff;
                      text-decoration: none;

                      &:hover {
                        text-decoration: underline;
                      }
                    `}
                  >
                    {text}
                  </a>
                </li>
              );
            })}
          </ul>
          <p
            css={css`
              color: #fff;
              font-size: 12px;

              ${mobile(({ clamp }) => css`
                font-size: ${clamp('10px', '12px')};
              `)}
            `}
          >
            &copy;2015 Speee, inc.
          </p>
        </div>
      </div>
    </div>
  );
};
